import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CanAccess,
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
} from "ra-core";
import { Navigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { ListCardsSkeleton, ListErrorState } from "../design/ListStates";
import type { CrmDataProvider } from "../providers/types";
import {
  AVAILABILITY_TIME_ZONE,
  WEEKDAYS,
  formatBlockTime,
  ownActiveBlocks,
  validateBlockDraft,
  type BlockValidationError,
} from "./availabilityBlocks";

const QUERY_KEY = "my_availability_blocks";
const RESOURCE = "advisor_availability_blocks";

/**
 * Almikia Epic EL (TASK-009): "Mi disponibilidad". An advisor who can receive leads (asesora,
 * lider) chooses the weekly blocks in which she wants new automatic leads. She sees and edits
 * ONLY her own blocks (RLS enforces it underneath); a block ends by setting `ended_at`, there
 * is no delete. No attendance, punctuality or comparison data exists anywhere on this screen.
 */
export const MyAvailability = () => (
  <CanAccess
    resource="advisor_availability"
    action="list"
    accessDenied={<Navigate to="/" replace />}
  >
    <MyAvailabilityContent />
  </CanAccess>
);

MyAvailability.path = "/my_availability";

const MyAvailabilityContent = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { identity, isPending: isIdentityPending } = useGetIdentity();
  const salesId = identity?.id;

  const [weekday, setWeekday] = useState<number>(1);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("13:00");
  const [error, setError] = useState<BlockValidationError | null>(null);
  const [saving, setSaving] = useState(false);

  const blocks = useQuery({
    queryKey: [QUERY_KEY, salesId],
    enabled: salesId != null,
    queryFn: () => dataProvider.getAdvisorAvailabilityBlocks(salesId),
  });

  if (blocks.error) {
    return (
      <ListErrorState
        message={translate("crm.common.list_error")}
        onRetry={() => blocks.refetch()}
      />
    );
  }
  if (isIdentityPending || salesId == null || !blocks.data) {
    return <ListCardsSkeleton rows={3} />;
  }

  const own = ownActiveBlocks(blocks.data, salesId);

  /** Runs one write, refreshes the list and reports the outcome in plain Spanish. */
  const save = async (write: () => Promise<unknown>) => {
    setSaving(true);
    try {
      await write();
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      notify("crm.availability.saved", { type: "success" });
    } catch {
      notify("crm.availability.save_error", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const draft = {
      weekday,
      start_time: startTime,
      end_time: endTime,
    };
    const problem = validateBlockDraft(draft, own);
    setError(problem);
    if (problem) return;
    await save(() =>
      dataProvider.create(RESOURCE, {
        data: {
          ...draft,
          sales_id: salesId,
          created_at: new Date().toISOString(),
        },
      }),
    );
  };

  const endBlock = (id: number | string) =>
    save(() =>
      dataProvider.update(RESOURCE, {
        id,
        data: { ended_at: new Date().toISOString() },
        previousData: { id },
      }),
    );

  return (
    <div className="mt-2 max-w-2xl mx-auto space-y-4 pb-8">
      <h1 className="px-1 text-xl font-semibold">
        {translate("crm.availability.title")}
      </h1>
      <p className="px-1 text-sm text-muted-foreground">
        {translate("crm.availability.intro")}
      </p>

      <Card>
        <CardContent className="space-y-3 p-4">
          <h2 className="font-medium">
            {translate("crm.availability.list_title")}
          </h2>
          <p className="text-sm text-muted-foreground">
            {translate("crm.availability.time_zone", {
              zone: AVAILABILITY_TIME_ZONE,
            })}
          </p>
          {own.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {translate("crm.availability.empty")}
            </p>
          ) : (
            <ul className="divide-y" data-testid="availability-list">
              {own.map((block) => (
                <li
                  key={block.id}
                  className="flex items-center justify-between gap-3 py-3"
                >
                  <span>
                    <span className="font-medium">
                      {translate(`crm.availability.weekday_${block.weekday}`)}
                    </span>{" "}
                    {translate("crm.availability.range", {
                      start: formatBlockTime(block.start_time),
                      end: formatBlockTime(block.end_time),
                    })}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-11 sm:h-8"
                    disabled={saving}
                    aria-label={translate("crm.availability.end_aria", {
                      day: translate(
                        `crm.availability.weekday_${block.weekday}`,
                      ),
                      start: formatBlockTime(block.start_time),
                    })}
                    onClick={() => endBlock(block.id)}
                  >
                    {translate("crm.availability.end")}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <form className="space-y-3" onSubmit={onSubmit} noValidate>
            <h2 className="font-medium">
              {translate("crm.availability.add_title")}
            </h2>
            <div className="space-y-2">
              <Label htmlFor="availability-weekday">
                {translate("crm.availability.weekday_label")}
              </Label>
              <select
                id="availability-weekday"
                className="h-11 w-full rounded-md border bg-background px-3 text-sm"
                value={weekday}
                onChange={(e) => setWeekday(Number(e.target.value))}
              >
                {WEEKDAYS.map((day) => (
                  <option key={day} value={day}>
                    {translate(`crm.availability.weekday_${day}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="availability-start">
                  {translate("crm.availability.start_label")}
                </Label>
                <Input
                  id="availability-start"
                  className="h-11 sm:h-9"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="availability-end">
                  {translate("crm.availability.end_label")}
                </Label>
                <Input
                  id="availability-end"
                  className="h-11 sm:h-9"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
            </div>
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {translate(`crm.availability.error_${error}`)}
              </p>
            ) : null}
            <Button type="submit" className="h-11 sm:h-9" disabled={saving}>
              {translate("crm.availability.add")}
            </Button>
          </form>
        </CardContent>
      </Card>

      <p className="px-1 text-sm text-muted-foreground">
        {translate("crm.availability.note")}
      </p>
    </div>
  );
};
